import { Components, customHooks } from "@thisgorilla/primate-ds";
import { AnimatePresence } from "framer-motion";
import { useRef, useState } from "react";
import { useTheme } from "styled-components";
import { useAppSelector } from "../../../../../../../redux";
import { SPRING_ANIMATION_CONFIG } from "../../../../../../../utils";
import { FlexRow } from "../../../../../../Common/Styles";
import { usePostCardContext } from "../../PostCardContent";
import { ReactionButtonStyles } from "./ReactionsButton.styled";
import { ReactionsTray } from "./ReactionsTray";

type From = "user" | "post";
export const ReactionsButton = () => {
  const reactionsRef = useRef<HTMLDivElement>(null);

  const [showReactionsTray, setShowReactionsTray] = useState(false);
  const [showEmojiAnimation, setShowEmojiAnimation] = useState(false);
  const [isExistingReactionsHovered, setIsExistingReactionsHovered] =
    useState(false);
  const { typography, Color } = useTheme();

  customHooks.useClickOutside({
    ref: reactionsRef,
    setState: setShowReactionsTray,
  });
  customHooks.useMouseOver({
    ref: reactionsRef,
    onMouseLeave: () => setShowReactionsTray(false),
    mouseLeaveDelay: 300,
    mouseEnterDelay: 200,
  });

  const { postId: post_id } = usePostCardContext();
  const { processed_posts } = useAppSelector((state) => state.newsfeed);
  const { reactions } = useAppSelector((state) => state.reactions);
  const post = processed_posts[post_id];
  const { total_curated_reactions, reaction_counts, user_reacted } = post;

  const emojiValues = Object.values(reaction_counts).slice(0, 3);
  const emojis = emojiValues.map(({ primary_emoji }) => primary_emoji);

  const reactionsCount = Number(`${total_curated_reactions}`);
  const isReactionsHavingStringCount = isNaN(reactionsCount);
  const hasReactions =
    isReactionsHavingStringCount || reactionsCount > 0 ? true : false;

  const EMPTY_REACTIONS_PLACEHOLDER = (
    <Components.Icon type="Smile" size="16px" />
  );

  const getEmojiContent = (from: From) => {
    if (!emojis || !emojis.length) return <></>;

    const showEmojisUponUserReaction =
      isReactionsHavingStringCount || reactionsCount > 1;
    const existingEmojis = emojis.map((emoji, index) => (
      <ReactionButtonStyles.EmojiWrapper
        key={`${emoji}-${index}`}
        $index={index}
      >
        {emoji}
      </ReactionButtonStyles.EmojiWrapper>
    ));

    const userReactedEmojis = showEmojisUponUserReaction ? (
      <ReactionButtonStyles.UserReactedEmojiContainer
        onMouseEnter={() => {
          setIsExistingReactionsHovered(true);
          setShowEmojiAnimation(false);
        }}
        onMouseLeave={() => setIsExistingReactionsHovered(false)}
      >
        {showReactionsTray ? (
          existingEmojis
        ) : (
          <Components.Tooltip
            type="label"
            label={`${total_curated_reactions} people reacted`}
            mode="dark"
          >
            <>{existingEmojis}</>
          </Components.Tooltip>
        )}

        {isExistingReactionsHovered && (
          <ReactionButtonStyles.CounterWrapper
            onMouseEnter={() => setIsExistingReactionsHovered(false)}
          >
            <Components.Counter
              color="Dark"
              count={total_curated_reactions}
              kind="Fill"
              size="Small"
            />
          </ReactionButtonStyles.CounterWrapper>
        )}
      </ReactionButtonStyles.UserReactedEmojiContainer>
    ) : (
      <></>
    );

    const defaultEmojis = (
      <ReactionButtonStyles.EmojisContainer className="emojis-container">
        {existingEmojis}
      </ReactionButtonStyles.EmojisContainer>
    );

    return (
      <FlexRow $gapInPx={8}>
        {from === "user" ? userReactedEmojis : defaultEmojis}
      </FlexRow>
    );
  };

  const reactionButtonProps = {
    onClick: () => {
      setShowReactionsTray((prev) => !prev);
      setShowEmojiAnimation(false);
    },
  };

  const getUserReactedButton = () => {
    if (!user_reacted.reacted) return <></>;
    const { reaction_type_id } = user_reacted;
    const selectedReaction = reactions[reaction_type_id];
    if (!selectedReaction) return <></>;
    const { background_color, hover_background_color } = selectedReaction;

    return (
      <ReactionButtonStyles.UserReactedButton
        $background={background_color}
        $hoverBackground={hover_background_color}
        {...reactionButtonProps}
        onMouseEnter={() => setShowEmojiAnimation(!showReactionsTray && true)}
        onMouseLeave={() => setShowEmojiAnimation(false)}
      >
        <ReactionButtonStyles.Emoji
          animate={{
            rotate: showEmojiAnimation ? -30 : 0,
            fontSize: showEmojiAnimation ? "28px" : "24px",
          }}
          transition={SPRING_ANIMATION_CONFIG}
        >
          {user_reacted.reaction_primary_reaction}
        </ReactionButtonStyles.Emoji>
        {hasReactions && getEmojiContent("user")}
      </ReactionButtonStyles.UserReactedButton>
    );
  };
  const getDefaultReactionButton = () => {
    return (
      <ReactionButtonStyles.ReactionButton {...reactionButtonProps}>
        {hasReactions ? getEmojiContent("post") : EMPTY_REACTIONS_PLACEHOLDER}
        {hasReactions && (
          <Components.Text
            text={total_curated_reactions}
            color={Color.text["text-primary"].value}
            style={{ tokenStyle: typography.Body.M }}
            lineHeight="line-height-50"
          />
        )}
      </ReactionButtonStyles.ReactionButton>
    );
  };

  return (
    <ReactionButtonStyles.Container ref={reactionsRef}>
      {user_reacted.reacted
        ? getUserReactedButton()
        : getDefaultReactionButton()}

      <AnimatePresence>
        {showReactionsTray && (
          <ReactionsTray onEmojiClick={() => setShowReactionsTray(false)} />
        )}
      </AnimatePresence>
    </ReactionButtonStyles.Container>
  );
};
